import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface ContextValue {
  nextPage: () => void;
  previousPage: () => void;
  setPage: (pageNumber: number) => void;
  reset: () => void;
  currentPageNumber: number;
}

interface ModalWizardNavigationProviderProps {
  children: React.ReactNode;
}
const ModalWizardNavigationContext = createContext<ContextValue | undefined>(
  undefined,
);

export function ModalWizardNavigationProvider({
  children,
}: ModalWizardNavigationProviderProps) {
  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const reset = useCallback(
    () => setCurrentPageNumber(0),
    [setCurrentPageNumber],
  );
  const nextPage = useCallback(() => {
    setCurrentPageNumber(currentPageNumber + 1);
  }, [setCurrentPageNumber, currentPageNumber]);
  const previousPage = useCallback(() => {
    setCurrentPageNumber(currentPageNumber - 1);
  }, [setCurrentPageNumber, currentPageNumber]);
  const setPage = useCallback(
    (pageNumber: number) => {
      setCurrentPageNumber(pageNumber);
    },
    [setCurrentPageNumber],
  );

  const contextValue = useMemo(
    () => ({ currentPageNumber, reset, nextPage, previousPage, setPage }),
    [currentPageNumber, reset, nextPage, previousPage, setPage],
  );

  return (
    <ModalWizardNavigationContext.Provider value={contextValue}>
      {children}
    </ModalWizardNavigationContext.Provider>
  );
}

export const useModalWizardNavigation = () => {
  const context = useContext(ModalWizardNavigationContext);

  if (!context) {
    throw new Error('Need to use the ModalWizardNavigationProvider!');
  }
  return context;
};
